import { Database, KeyRound, Mail, Server, ShieldCheck } from "lucide-react";
import { MasterShell } from "@/shared/layouts/MasterShell.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { useAuth } from "@/features/auth/auth-context.jsx";
import { useMasterStats } from "./use-master.js";
import { formatNumber } from "@/shared/lib/format.js";

/**
 * Configurações globais da plataforma.
 *
 * As chaves de verdade (SESSION_SECRET, APP_ENCRYPTION_KEY, RESEND_API_KEY)
 * ficam no `.env` do servidor e nunca aparecem aqui — nem mascaradas. Esta
 * tela mostra o estado da instalação e as políticas em vigor.
 */
export function MasterSettingsPage() {
  const { user } = useAuth();
  const { data: stats } = useMasterStats();

  return (
    <MasterShell title="Configurações Globais" subtitle="Políticas e estado da plataforma">
      <div className="mx-auto max-w-5xl space-y-6 p-6">
        {/* -------------------------------------------- políticas em vigor */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 font-display text-base">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Políticas de segurança
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Aplicadas pelo servidor a todas as clínicas.
            </p>
          </CardHeader>

          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Politica
              titulo="Senhas"
              descricao="Armazenadas com bcrypt (12 rounds). Nunca em texto puro."
              ativa
            />
            <Politica
              titulo="Sessão"
              descricao="Cookie httpOnly + SameSite=Lax, revogável no logout."
              ativa
            />
            <Politica
              titulo="Credenciais de integração"
              descricao="Cifradas com AES-256-GCM; jamais devolvidas ao navegador."
              ativa
            />
            <Politica
              titulo="Webhook da Meta"
              descricao="Assinatura X-Hub-Signature-256 conferida em cada evento."
              ativa
            />
            <Politica
              titulo="Isolamento entre clientes"
              descricao="Toda consulta filtra por clinic_id no servidor."
              ativa
            />
            <Politica
              titulo="Auditoria"
              descricao="Login, alterações, exclusões e acessos Master registrados."
              ativa
            />
          </CardContent>
        </Card>

        {/* --------------------------------------------------- instalação */}
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 font-display text-base">
                <Database className="h-4 w-4 text-primary" />
                Dados da instalação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Linha rotulo="Clientes cadastrados" valor={formatNumber(stats?.clinics.total ?? 0)} />
              <Linha rotulo="Usuários na plataforma" valor={formatNumber(stats?.usage.usuarios ?? 0)} />
              <Linha rotulo="Leads gerenciados" valor={formatNumber(stats?.usage.leads ?? 0)} />
              <Linha rotulo="Mensagens trocadas" valor={formatNumber(stats?.usage.mensagens ?? 0)} />
              <Linha rotulo="Jornadas criadas" valor={formatNumber(stats?.usage.jornadas ?? 0)} />
              <Linha
                rotulo="WhatsApp conectados"
                valor={formatNumber(stats?.usage.whatsappAtivos ?? 0)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 font-display text-base">
                <Server className="h-4 w-4 text-primary" />
                Sua sessão
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Linha rotulo="Usuário" valor={user?.fullName} />
              <Linha rotulo="E-mail" valor={user?.email} />
              <Linha rotulo="Perfil" valor={user?.roleLabel} />
            </CardContent>
          </Card>
        </div>

        {/* ------------------------------------------- variáveis do servidor */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 font-display text-base">
              <KeyRound className="h-4 w-4 text-warning" />
              Configuração do servidor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground">
              <p className="mb-3 flex items-center gap-2 font-medium text-foreground">
                <Mail className="h-4 w-4" />
                Estas chaves vivem no arquivo <code className="rounded bg-background px-1">.env</code>
              </p>
              <ul className="space-y-1.5 font-mono text-xs">
                <li>SESSION_SECRET — assina o cookie de sessão</li>
                <li>APP_ENCRYPTION_KEY — cifra as credenciais de integração</li>
                <li>RESEND_API_KEY — envio dos e-mails de credenciais</li>
                <li>WHATSAPP_APP_SECRET — verificação do webhook da Meta</li>
              </ul>
              <p className="mt-3 text-xs">
                Elas nunca são exibidas nesta tela, nem mascaradas: um painel web não é lugar para
                segredo de servidor. Para alterá-las, edite o <code>.env</code> e reinicie a API.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </MasterShell>
  );
}

function Politica({ titulo, descricao, ativa }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border/60 p-3">
      <ShieldCheck className={`mt-0.5 h-4 w-4 shrink-0 ${ativa ? "text-success" : "text-muted-foreground"}`} />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{titulo}</p>
          {ativa && (
            <Badge variant="success" className="text-[10px]">
              ativa
            </Badge>
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{descricao}</p>
      </div>
    </div>
  );
}

function Linha({ rotulo, valor }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-2 last:border-0">
      <span className="text-muted-foreground">{rotulo}</span>
      <span className="truncate font-medium">{valor || "—"}</span>
    </div>
  );
}
